'use client'

import React, {useState} from 'react';
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import Image from "next/image";
import NoImage from "@/public/no-image.jpg";

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import {cn} from "@/lib/utils";

import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {VisuallyHidden} from "@radix-ui/react-visually-hidden";
import {Tabs, TabsContent} from "@/components/ui/tabs";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {ScrollArea} from "@radix-ui/react-scroll-area";
import {SingleProductEnum} from "@/constants/commerce-enum";


interface ProductImageGalleryProps {
    mediaData: {
        url : string
    }[]
    productName: string,
}

function SliderImageMobileComponent({mediaData, productName}: ProductImageGalleryProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedImage, setSelectedImage] = useState(mediaData[0]?.url)
    const [currentIndex, setCurrentIndex] = useState(0)

    if (!mediaData) return null;

    const handleImageClick = (image: string, index: number) => {
        setSelectedImage(image)
        setCurrentIndex(index)
    }

    const handlePrevious = () => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1
            setCurrentIndex(newIndex)
            setSelectedImage(mediaData[newIndex]?.url)
        }
    }

    const handleNext = () => {
        if (currentIndex < mediaData.length - 1) {
            const newIndex = currentIndex + 1
            setCurrentIndex(newIndex)
            setSelectedImage(mediaData[newIndex]?.url)
        }
    }

    const SliderComponent = ({isModal}: any) => {
        return (
            <Swiper
                slidesPerView={1}
                spaceBetween={10}
                pagination={{
                    clickable: true,
                }}
                modules={[Pagination]}
                className="mySwiper mobile-single-product-slider w-full h-full overflow-hidden"
            >
                {mediaData?.map((item, index: number) => (
                    <SwiperSlide key={index}>
                        <figure className="w-full h-full flex flex-wrap items-center justify-center">
                            <Image
                                quality={80}
                                onClick={() => !isModal && setIsOpen(true)}
                                src={item?.url ?? NoImage}
                                alt={"product-title"}
                                title={"product-title"}
                                width={200}
                                height={200}
                                loading={"lazy"}
                                className="object-contain h-[200px] w-[200px] cursor-zoom-in"
                            />
                        </figure>
                    </SwiperSlide>
                ))}
            </Swiper>

        )
    }


    if (!mediaData) {
        return null;
    }
    return (
        <>
            <SliderComponent mediaData={mediaData}/>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="w-11/12 max-h-[80vh] rounded-xl">
                    <DialogHeader>
                        <DialogTitle className={cn("text-right text-lg font-semibold")}>
                            {SingleProductEnum?.images}
                        </DialogTitle>
                    </DialogHeader>
                    <VisuallyHidden>
                        <DialogTitle>{`${productName} Image Gallery`}</DialogTitle>
                    </VisuallyHidden>
                    <Tabs defaultValue="official" className="">
                        <TabsContent value="official">
                            {mediaData ? (
                                <ImageGallery
                                    setIsOpen={setIsOpen}
                                    selectedImage={selectedImage}
                                    productName={productName}
                                    handlePrevious={handlePrevious}
                                    handleNext={handleNext}
                                    mediaData={mediaData}
                                    handleImageClick={handleImageClick}
                                    currentIndex={currentIndex}
                                />
                            ) : null}
                        </TabsContent>
                        <TabsContent value="customer">
                            <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                                {SingleProductEnum?.NoCustomeImagesAvailable}
                            </div>
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>
        </>
    )


}

export default SliderImageMobileComponent;


const ImageGallery = ({setIsOpen , selectedImage ,productName , handlePrevious  , handleNext , mediaData , handleImageClick ,currentIndex } : any) => (
    <div className={cn(
        "flex flex-wrap items-center justify-center pt-4 min-h-[400px] w-full ring-0"
    )}>
        <div
            className="w-full flex items-center justify-center"
            onClick={() => setIsOpen(true)}
        >
            <Image
                src={selectedImage}
                alt={`${productName} image`}
                title={`${productName} image`}
                width={300}
                height={300}
                loading={"lazy"}
                className="h-full object-contain p-2 rounded-2xl "
            />
            <>
                <button
                    onClick={handlePrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow-none border"
                    aria-label="Previous image"
                >
                    <ChevronLeft className="w-5 h-5"/>
                </button>
                <button
                    onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow-none border"
                    aria-label="Next image"
                >
                    <ChevronRight className="w-5 h-5"/>
                </button>
            </>
        </div>
        <div className="flex justify-center p-4  w-11/12 relative">
            <ScrollArea className="flex space-x-2 overflow-hidden  w-full items-center justify-center">
                {mediaData?.map((item : any, index : any) => (
                    <button
                        key={index}
                        onClick={() => handleImageClick(item?.url, index)}
                        className={cn(
                            "flex-shrink-0 border rounded-lg overflow-hidden",
                            currentIndex === index ? "border-orange-hover" : "border-gray-100",
                        )}
                        aria-label={`View ${productName} image ${index + 1}`}
                    >
                        <Image
                            src={item?.url ?? NoImage}
                            alt={`${productName} thumbnail ${index + 1}`}
                            title={`${productName} thumbnail ${index + 1}`}
                            width={64}
                            height={64}
                            loading={"lazy"}
                            className="w-[64px] h-[64px] object-contain p-1"
                        />
                    </button>
                ))}
            </ScrollArea>
        </div>
    </div>
)
